import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Grid,
  Fab,
  Avatar,
  useTheme,
  useMediaQuery,
  Divider,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Category as CategoryIcon,
  Close,
  Palette,
  Code,
  Visibility,
  VisibilityOff,
  Save,
  Cancel
} from '@mui/icons-material';

// Importar ícones do Material-UI
import * as Icons from '@mui/icons-material';

import { Category, CategoryFormData } from '../types';
import categoryService from '../services/categoryService';

interface CategoryManagerProps {
  open: boolean;
  onClose: () => void;
  onCategoryChange?: () => void;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({
  open,
  onClose,
  onCategoryChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estados principais
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Estados do formulário
  const [formOpen, setFormOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>({
    nome: '',
    codigo: '',
    descricao: '',
    ativo: true,
    cor: '#1976d2',
    icone: 'Category'
  });

  // Estados de feedback
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Estados de seleção de cor e ícone
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);

  // Carregar categorias
  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryService.getCategories({
        search: searchTerm || undefined,
        ativo: showInactive ? undefined : true
      });

      if (response.success) {
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      showAlert('error', 'Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadCategories();
    }
  }, [open, searchTerm, showInactive]);

  const showAlert = (type: 'success' | 'error', message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  // Manipuladores do formulário
  const handleFormSubmit = async () => {
    const errors = categoryService.validateCategoryData(formData);
    setValidationErrors(errors);

    if (errors.length > 0) {
      return;
    }

    try {
      if (selectedCategory) {
        const response = await categoryService.updateCategory(selectedCategory.id, formData);
        if (response.success) {
          showAlert('success', 'Categoria atualizada com sucesso!');
        }
      } else {
        const response = await categoryService.createCategory(formData);
        if (response.success) {
          showAlert('success', 'Categoria criada com sucesso!');
        }
      }

      setFormOpen(false);
      resetForm();
      loadCategories();
      onCategoryChange?.();
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Erro ao salvar categoria');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      codigo: '',
      descricao: '',
      ativo: true,
      cor: '#1976d2',
      icone: 'Category'
    });
    setSelectedCategory(null);
    setValidationErrors([]);
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setFormData({
      nome: category.nome,
      codigo: category.codigo,
      descricao: category.descricao || '',
      ativo: category.ativo,
      cor: category.cor || '#1976d2',
      icone: category.icone || 'Category'
    });
    setFormOpen(true);
  };

  const handleDelete = async (category: Category) => {
    if (window.confirm(`Tem certeza que deseja deletar a categoria "${category.nome}"?`)) {
      try {
        const response = await categoryService.deleteCategory(category.id);
        if (response.success) {
          showAlert('success', 'Categoria deletada com sucesso!');
          loadCategories();
          onCategoryChange?.();
        }
      } catch (error: any) {
        showAlert('error', error.response?.data?.message || 'Erro ao deletar categoria');
      }
    }
  };

  const handleToggleStatus = async (category: Category) => {
    try {
      const response = await categoryService.toggleStatus(category.id);
      if (response.success) {
        showAlert('success', `Categoria ${!category.ativo ? 'ativada' : 'desativada'} com sucesso!`);
        loadCategories();
        onCategoryChange?.();
      }
    } catch (error: any) {
      showAlert('error', error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  // Gerar código automaticamente baseado no nome
  const handleNameChange = (nome: string) => {
    setFormData(prev => ({
      ...prev,
      nome,
      codigo: selectedCategory ? prev.codigo : categoryService.generateCodeFromName(nome)
    }));
  };

  // Renderizar ícone dinamicamente
  const renderIcon = (iconName: string | undefined, size: 'small' | 'medium' = 'medium') => {
    const IconComponent = (Icons as any)[iconName || 'Category'] || Icons.Category;
    return <IconComponent fontSize={size} />;
  };

  // Renderizar tabela de categorias
  const renderCategoriesTable = () => (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Categoria</TableCell>
            <TableCell>Código</TableCell>
            <TableCell align="center">Status</TableCell>
            <TableCell align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <CircularProgress size={40} />
              </TableCell>
            </TableRow>
          ) : categories.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                Nenhuma categoria encontrada
              </TableCell>
            </TableRow>
          ) : (
            categories.map((category) => (
              <TableRow key={category.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: category.cor || '#1976d2',
                        fontSize: '0.875rem'
                      }}
                    >
                      {renderIcon(category.icone || 'Category', 'small')}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        {category.nome}
                      </Typography>
                      {category.descricao && (
                        <Typography variant="caption" color="text.secondary">
                          {category.descricao}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {category.codigo}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={category.ativo ? 'Ativa' : 'Inativa'}
                    size="small"
                    color={category.ativo ? 'success' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" gap={0.5} justifyContent="center">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(category)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={category.ativo ? 'Desativar' : 'Ativar'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleStatus(category)}
                      >
                        {category.ativo ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Deletar">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(category)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Renderizar seletor de cores
  const renderColorPicker = () => (
    <Grid container spacing={1} sx={{ mt: 1 }}>
      {categoryService.getDefaultColors().map((color) => (
        <Grid item key={color}>
          <Box
            sx={{
              width: 32,
              height: 32,
              backgroundColor: color,
              borderRadius: 1,
              cursor: 'pointer',
              border: formData.cor === color ? '3px solid #000' : '1px solid #ccc',
              '&:hover': {
                transform: 'scale(1.1)'
              }
            }}
            onClick={() => setFormData(prev => ({ ...prev, cor: color }))}
          />
        </Grid>
      ))}
    </Grid>
  );

  // Renderizar seletor de ícones
  const renderIconPicker = () => (
    <Grid container spacing={1} sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
      {categoryService.getDefaultIcons().map((icon) => (
        <Grid item key={icon.name}>
          <Tooltip title={icon.label}>
            <Box
              sx={{
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                cursor: 'pointer',
                border: formData.icone === icon.name ? '2px solid primary.main' : '1px solid #ccc',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
              onClick={() => setFormData(prev => ({ ...prev, icone: icon.name }))}
            >
              {renderIcon(icon.name, 'small')}
            </Box>
          </Tooltip>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <>
      {/* Modal principal */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <CategoryIcon />
              <Typography variant="h6">Gerenciar Categorias</Typography>
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Alert */}
          {alert && (
            <Alert severity={alert.type} sx={{ mb: 2 }}>
              {alert.message}
            </Alert>
          )}

          {/* Controles */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
            <TextField
              size="small"
              placeholder="Buscar categorias..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
              }
              label="Mostrar inativas"
            />

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                resetForm();
                setFormOpen(true);
              }}
            >
              Nova Categoria
            </Button>
          </Box>

          {/* Tabela */}
          {renderCategoriesTable()}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal do formulário */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {selectedCategory ? 'Editar Categoria' : 'Nova Categoria'}
        </DialogTitle>

        <DialogContent>
          {/* Erros de validação */}
          {validationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Nome */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                value={formData.nome}
                onChange={(e) => handleNameChange(e.target.value)}
                required
              />
            </Grid>

            {/* Código */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Código"
                value={formData.codigo}
                onChange={(e) => setFormData(prev => ({ ...prev, codigo: e.target.value }))}
                required
                helperText="Usado internamente no sistema"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Code />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Descrição */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={formData.descricao}
                onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                multiline
                rows={2}
              />
            </Grid>

            {/* Cor */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Cor da Categoria
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: formData.cor,
                      borderRadius: 1,
                      border: '1px solid #ccc',
                      cursor: 'pointer'
                    }}
                    onClick={() => setColorPickerOpen(!colorPickerOpen)}
                  />
                  <TextField
                    size="small"
                    value={formData.cor}
                    onChange={(e) => setFormData(prev => ({ ...prev, cor: e.target.value }))}
                    placeholder="#RRGGBB"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Palette />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>
                {colorPickerOpen && renderColorPicker()}
              </Box>
            </Grid>

            {/* Ícone */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Ícone da Categoria
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: formData.cor,
                      borderRadius: 1,
                      color: 'white',
                      cursor: 'pointer'
                    }}
                    onClick={() => setIconPickerOpen(!iconPickerOpen)}
                  >
                    {renderIcon(formData.icone, 'small')}
                  </Box>
                  <TextField
                    size="small"
                    value={formData.icone}
                    onChange={(e) => setFormData(prev => ({ ...prev, icone: e.target.value }))}
                    placeholder="Nome do ícone"
                  />
                </Box>
                {iconPickerOpen && renderIconPicker()}
              </Box>
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.ativo}
                    onChange={(e) => setFormData(prev => ({ ...prev, ativo: e.target.checked }))}
                  />
                }
                label="Categoria ativa"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setFormOpen(false)} startIcon={<Cancel />}>
            Cancelar
          </Button>
          <Button
            onClick={handleFormSubmit}
            variant="contained"
            startIcon={<Save />}
            disabled={!formData.nome || !formData.codigo}
          >
            {selectedCategory ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CategoryManager; 